import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
  IconButton,
  Fade,
  SlideFade,
  Skeleton,
  Tooltip,
  useDisclosure,
  Badge
} from '@chakra-ui/react';
import { FaCheckCircle, FaCircle, FaArrowUp, FaBookOpen, FaFileAlt } from 'react-icons/fa';

/**
 * Neighborhood Council Regulations Page
 * 
 * Displays the official regulations for neighborhood council formation
 * in a modern, mobile-friendly format with chapter-based navigation.
 */
const NeighborhoodCouncilRegulations = () => {
  const [activeChapter, setActiveChapter] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { onOpen } = useDisclosure({ defaultIsOpen: false });
  
  const chapterRefs = {
    0: useRef(null),
    1: useRef(null),
    2: useRef(null),
    3: useRef(null)
  };
  
  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('beige.200', 'beige.700');
  const headingColor = useColorModeValue('brown.700', 'brown.200');
  const subheadingColor = useColorModeValue('teal.600', 'teal.300');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const badgeBg = useColorModeValue('lightBeige.100', 'lightBeige.700');
  const cardBg = useColorModeValue('beige.50', 'gray.700');
  const accentColor = useColorModeValue('teal.500', 'teal.300');
  
  // Table styling
  const tableHeadBg = useColorModeValue('beige.100', 'beige.700');
  const tableHoverBg = useColorModeValue('beige.50', 'gray.600');
  
  // Handle scroll to chapter
  const scrollToChapter = (chapterNum) => {
    setActiveChapter(chapterNum);
    if (chapterRefs[chapterNum] && chapterRefs[chapterNum].current) {
      chapterRefs[chapterNum].current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Handle scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Check scroll position for showing/hiding scroll-to-top button
  const handleScroll = () => {
    if (window.scrollY > 300) {
      setShowScrollTop(true);
    } else {
      setShowScrollTop(false);
    }
  };
  
  // Simulate loading content
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      onOpen();
    }, 800);
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onOpen]);
  
  return (
    <Container maxW="container.lg" py={8} dir="rtl">
      {/* Scroll to top button */}
      <Fade in={showScrollTop}>
        <IconButton
          aria-label="Scroll to top"
          icon={<FaArrowUp />}
          size="md"
          colorScheme="teal"
          variant="solid"
          position="fixed"
          bottom="20px"
          left={{ base: "20px", md: "40px" }}
          zIndex={2}
          opacity={0.8}
          _hover={{ opacity: 1 }}
          onClick={scrollToTop}
          boxShadow="md"
          borderRadius="full"
        />
      </Fade>
      
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box 
          p={6} 
          bg={useColorModeValue('beige.50', 'gray.800')}
          borderRadius="lg"
          boxShadow="md"
          borderRight="4px solid"
          borderColor="teal.500"
          ref={chapterRefs[0]}
          transition="all 0.3s ease"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
        >
          <Heading 
            as="h1" 
            size="xl" 
            mb={2}
            color={headingColor}
            fontWeight="bold"
          >
            لائحة تشكيل مجالس مراكز الأحياء
          </Heading>
          <Text 
            fontSize="lg" 
            color={subheadingColor}
            fontWeight="medium"
          >
            جمعية مراكز الأحياء بمنطقة مكة المكرمة 2025
          </Text>
        </Box>
        
        {/* Table of Contents */}
        <Skeleton isLoaded={!isLoading} fadeDuration={1} borderRadius="lg">
          <SlideFade in={!isLoading} offsetY="20px">
            <Box 
              p={6} 
              bg={bgColor} 
              borderRadius="lg" 
              boxShadow="sm"
              borderTop="1px solid"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{ boxShadow: 'md' }}
            >
              <HStack mb={4} spacing={3}>
                <FaBookOpen color={accentColor} />
                <Heading as="h2" size="md" color={subheadingColor}>
                  قائمة المحتويات
                </Heading>
                <Badge colorScheme="teal" fontSize="0.8em" borderRadius="full" px={2}>
                  {activeChapter > 0 ? activeChapter : ""}
                </Badge>
              </HStack>
              <List spacing={3}>
                {/* Chapter 1 with sub-articles */}
                <Box>
                  <Tooltip label="عرض الفصل الأول" placement="start" hasArrow>
                    <ListItem 
                      p={2} 
                      _hover={{ bg: 'beige.50', transform: 'translateX(-5px)' }} 
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => scrollToChapter(1)}
                      fontWeight={activeChapter === 1 ? "bold" : "normal"}
                      transition="all 0.2s ease"
                      bg={activeChapter === 1 ? badgeBg : 'transparent'}
                      borderRight={activeChapter === 1 ? '3px solid' : 'none'}
                      borderColor={activeChapter === 1 ? 'brown.500' : 'transparent'}
                    >
                      <HStack>
                        <ListIcon as={activeChapter === 1 ? FaCheckCircle : FaCircle} color="brown.500" />
                        <Text>الفصل الأول: التعريفات</Text>
                      </HStack>
                    </ListItem>
                  </Tooltip>
                  
                  {/* Sub-articles for Chapter 1 */}
                  {activeChapter === 1 && (
                    <List pr={8} mt={2} spacing={1} fontSize="sm">
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'brown.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                        onClick={() => scrollToChapter(1)}
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="brown.400" fontSize="xs" />
                          <Text>المادة الأولى: التعريفات</Text>
                        </HStack>
                      </ListItem>
                    </List>
                  )}
                </Box>
                
                {/* Chapter 2 with sub-articles */}
                <Box>
                  <Tooltip label="عرض الفصل الثاني" placement="start" hasArrow>
                    <ListItem 
                      p={2} 
                      _hover={{ bg: 'beige.50', transform: 'translateX(-5px)' }} 
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => scrollToChapter(2)}
                      fontWeight={activeChapter === 2 ? "bold" : "normal"}
                      transition="all 0.2s ease"
                      bg={activeChapter === 2 ? badgeBg : 'transparent'}
                      borderRight={activeChapter === 2 ? '3px solid' : 'none'}
                      borderColor={activeChapter === 2 ? 'teal.500' : 'transparent'}
                    >
                      <HStack>
                        <ListIcon as={activeChapter === 2 ? FaCheckCircle : FaCircle} color="teal.500" />
                        <Text>الفصل الثاني: التشكيل الإداري لمجلس الحي</Text>
                      </HStack>
                    </ListItem>
                  </Tooltip>
                  
                  {/* Sub-articles for Chapter 2 */}
                  {activeChapter === 2 && (
                    <List pr={8} mt={2} spacing={1} fontSize="sm">
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'teal.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                        onClick={() => scrollToChapter(2)}
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="teal.400" fontSize="xs" />
                          <Text>المادة الثانية: التشكيل الإداري</Text>
                        </HStack>
                      </ListItem>
                    </List>
                  )}
                </Box>
                
                {/* Chapter 3 with sub-articles */}
                <Box>
                  <Tooltip label="عرض الفصل الثالث" placement="start" hasArrow>
                    <ListItem 
                      p={2} 
                      _hover={{ bg: 'beige.50', transform: 'translateX(-5px)' }} 
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => scrollToChapter(3)}
                      fontWeight={activeChapter === 3 ? "bold" : "normal"}
                      transition="all 0.2s ease"
                      bg={activeChapter === 3 ? badgeBg : 'transparent'}
                      borderRight={activeChapter === 3 ? '3px solid' : 'none'}
                      borderColor={activeChapter === 3 ? 'mint.500' : 'transparent'}
                    >
                      <HStack>
                        <ListIcon as={activeChapter === 3 ? FaCheckCircle : FaCircle} color="mint.500" />
                        <Text>الفصل الثالث: مجلس الحي</Text>
                      </HStack>
                    </ListItem>
                  </Tooltip>
                  
                  {/* Sub-articles for Chapter 3 */}
                  {activeChapter === 3 && (
                    <List pr={8} mt={2} spacing={1} fontSize="sm">
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'mint.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="mint.400" fontSize="xs" />
                          <Text>المادة الثالثة: مجلس مركز الحي</Text>
                        </HStack>
                      </ListItem>
                      
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'mint.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="mint.400" fontSize="xs" />
                          <Text>المادة الرابعة: شروط التقديم</Text>
                        </HStack>
                      </ListItem>
                      
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'mint.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="mint.400" fontSize="xs" />
                          <Text>المادة الخامسة: فقدان العضوية</Text>
                        </HStack>
                      </ListItem>
                      
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'mint.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="mint.400" fontSize="xs" />
                          <Text>المادة السادسة: مهام مجلس الحي</Text>
                        </HStack>
                      </ListItem>
                      
                      <ListItem 
                        p={1} 
                        _hover={{ color: 'mint.600', fontWeight: 'medium' }} 
                        cursor="pointer"
                      >
                        <HStack>
                          <ListIcon as={FaFileAlt} color="mint.400" fontSize="xs" />
                          <Text>المادة السابعة: مدة العضوية</Text>
                        </HStack>
                      </ListItem>
                    </List>
                  )}
                </Box>
              </List>
            </Box>
          </SlideFade>
        </Skeleton>
        
        {/* Chapter 1: Definitions */}
        <Skeleton isLoaded={!isLoading} fadeDuration={1} borderRadius="lg">
          <SlideFade in={!isLoading && (activeChapter === 0 || activeChapter === 1)} offsetY="20px">
            {activeChapter === 0 || activeChapter === 1 ? (
            <Box 
              p={6} 
              bg={bgColor} 
              borderRadius="lg" 
              boxShadow="md"
              borderRight="3px solid"
              borderColor="brown.500"
              mt={4}
              ref={chapterRefs[1]}
              transition="all 0.3s ease"
              _hover={{ boxShadow: 'lg' }}
            >
              <Heading as="h2" size="lg" mb={4} color={headingColor}>
                الفصل الأول: التعريفات
              </Heading>
              <Divider mb={6} borderColor={borderColor} />
              
              <Box mb={6}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الأولى
                </Heading>
                <Text mb={4} color={textColor}>
                  يقصد بالكلمات والعبارات الآتية ــ في تطبيق أحكام هذه اللائحة ــ المعاني المبينة أمام كل منها ما لم يدل سياق النص على خلاف ذلك:
                </Text>
                
                <VStack spacing={4} align="stretch" pr={4}>
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>الجمعية:</Text>
                    <Text>جمعية مراكز الأحياء بمنطقة مكة المكرمة تأسست بالقرار الوزاري رقم (73739)، وتاريخ 11/06/1425هـ.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>الجمعية العمومية:</Text>
                    <Text>الجمعية العمومية لجمعية مراكز الأحياء هي أعلى سلطة في الجمعية، وقراراتها ملزمة لجميع الأعضاء ولبقية أجهزة الجمعية، وتكون عضوية الجمعية العمومية وفق اللائحة الأساسية للجمعية والمعتمدة من المركز الوطني لتنمية القطاع غير الربحي.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>مجلس الإدارة:</Text>
                    <Text>مجلس إدارة جمعية مراكز الأحياء على مستوى منطقة مكة المكرمة.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>رئيس مجلس الإدارة:</Text>
                    <Text>هو العضو المنتخب من مجلس إدارة جمعية مراكز الأحياء ويتولى رئاسة اجتماعاته ويمثل الجمعية أمام الجهات ذات العلاقة ويشرف على تنفيذ قرارات المجلس.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>لجنة المراجعة الداخلية:</Text>
                    <Text>لجنة منبثقة من مجلس الإدارة تعنى بمهام المراجعة الداخلية على جميع مستويات الجمعية.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>الرئيس التنفيذي:</Text>
                    <Text>المسؤول عن الجهاز التنفيذي بالجمعية بمنطقة مكة المكرمة.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>مدير فرع الجمعية:</Text>
                    <Text>المسؤول التنفيذي عن فرع الجمعية في المدينة أو المحافظة.</Text>
                  </Box>
                  
                  <Box p={4} bg={badgeBg} borderRadius="md">
                    <Text fontWeight="bold" mb={1}>مجلس مركز الحي:</Text>
                    <Text>هو المجلس الذي يمثل سكان الأحياء التابعين للنطاق الجغرافي للحي مركز الحي ويهتم بتلبية احتياجات الأهالي.</Text>
                  </Box>
                </VStack>
              </Box>
            </Box>
            ) : null}
          </SlideFade>
        </Skeleton>
        
        {/* Chapter 2: Administrative Formation */}
        <Skeleton isLoaded={!isLoading} fadeDuration={1} borderRadius="lg">
          <SlideFade in={!isLoading && activeChapter === 2} offsetY="20px">
            {activeChapter === 2 ? (
            <Box 
              p={6} 
              bg={bgColor} 
              borderRadius="lg" 
              boxShadow="md"
              borderRight="3px solid"
              borderColor="teal.500"
              mt={4}
              ref={chapterRefs[2]}
              transition="all 0.3s ease"
              _hover={{ boxShadow: 'lg' }}
            >
              <Heading as="h2" size="lg" mb={4} color={headingColor}>
                الفصل الثاني: التشكيل الإداري لمجلس الحي
              </Heading>
              <Divider mb={6} borderColor={borderColor} />
              
              <Box mb={8}>
                <Heading as="h3" size="md" mb={4} color={subheadingColor}>
                  المادة الثانية: التشكيل الإداري
                </Heading>
                
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    تكوين المجلس
                  </Heading>
                  <Text mb={3}>يتكون مجلس مركز الحي من الآتي:</Text>
                  <UnorderedList spacing={2} pr={4}>
                    <ListItem>رئيس المجلس لمركز الحي ويكون مرجعه الإداري والفني مدير فرع الجمعية</ListItem>
                    <ListItem>أعضاء المجلس (لا يقل عن خمسة ولا يزيد عن تسعة أعضاء)</ListItem>
                  </UnorderedList>
                </Box>
                
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md"
                  boxShadow="sm"
                >
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    إجراءات تعيين مجلس الحي
                  </Heading>
                  <OrderedList spacing={3} pr={4}>
                    <ListItem>
                      <Text>التأكد من أن المتقدمين لعضوية مجلس الحي من أهالي الحي من خلال مطابقة العنوان الوطني للمتقدم</Text>
                    </ListItem>
                    <ListItem>
                      <Text>تعبئة استمارة العضوية مع إرفاق صورة من الهوية والسيرة الذاتية وصورة من سداد رسوم العضوية</Text>
                    </ListItem>
                    <ListItem>
                      <Text>دراسة فرع الجمعية لطلب تعيين مجلس الحي والرفع للإدارة الرئيسية</Text>
                    </ListItem>
                    <ListItem>
                      <Text>يتم إصدار قرار تعيين الأعضاء من قبل الرئيس التنفيذي للجمعية وفق توصيات مدير الفرع وبعد الانتهاء من إجراءات المسح الأمني</Text>
                    </ListItem>
                    <ListItem>
                      <Text>يختار مجلس الحي في أول اجتماع له رئيساً ونائباً من أعضائه</Text>
                    </ListItem>
                  </OrderedList>
                </Box>
              </Box>
          </Box>
            ) : null}
          </SlideFade>
        </Skeleton>
        
        {/* Chapter 3: Council */}
        <Skeleton isLoaded={!isLoading} fadeDuration={1} borderRadius="lg">
          <SlideFade in={!isLoading && activeChapter === 3} offsetY="20px">
            {activeChapter === 3 ? (
            <Box 
              p={6} 
              bg={bgColor} 
              borderRadius="lg" 
              boxShadow="md"
              borderRight="3px solid"
              borderColor="mint.500"
              mt={4}
              ref={chapterRefs[3]}
              transition="all 0.3s ease"
              _hover={{ boxShadow: 'lg' }}
            >
              <Heading as="h2" size="lg" mb={4} color={headingColor}>
                الفصل الثالث: مجلس الحي
              </Heading>
              <Divider mb={6} borderColor={borderColor} />
              
              {/* Article 3 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الثالثة: مجلس مركز الحي
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Text mb={4}>
                    مجموعة من أعيان الحي لا يقل عددهم عن خمسة أعضاء ولا يزيد عن تسعة أعضاء من سكان الحي الذي يقع في نطاقه الجغرافي مركز الحي، ويصدر بتعيينهم قرار من الرئيس التنفيذي للجمعية، ويعد المجلس داعماً لمركز الحي مما يسهم في تحقيق أهداف الجمعية.
                  </Text>
                  <Text>
                    ويتكون المجلس من رئيس ونائب ومجموعة من الأعضاء، وترتبط مرجعيته بمدير فرع الجمعية.
                  </Text>
                </Box>
              </Box>
            
            {/* Article 4 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الرابعة: شروط التقديم على مجلس الحي
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Text mb={3}>أن تنطبق على المتقدم الشروط التالية:</Text>
                  <UnorderedList spacing={2} pr={4}>
                    <ListItem>أن يكون من سكان الحي كما يوضحه العنوان الوطني للعضو</ListItem>
                    <ListItem>أن لا يقل عمره عن (18) ثمانية عشر عاماً</ListItem>
                    <ListItem>أن يكون من ذوي السمعة الحسنة</ListItem>
                    <ListItem>أن يكون غير محكوم عليه بإدانة في جريمة مخلة بالشرف أو الأمانة ما لم يكن قد رد إليه اعتباره</ListItem>
                    <ListItem>أن يكون كامل الأهلية المعتبرة شرعاً</ListItem>
                    <ListItem>أن يكون قد سدد الحد الأدنى للاشتراك السنوي وفق ما تقرره لوائح الجمعية</ListItem>
                    <ListItem>أن يكون له دور في خدمة المجتمع وقد سجل في المنصة الوطنية للتطوع</ListItem>
                    <ListItem>تقديم برنامج لمجلس الحي يوضح الإسهامات في تقديم الخدمات لمركز الحي</ListItem>
                  </UnorderedList>
                </Box>
              </Box>
            
            
            {/* Article 5 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الخامسة: فقدان العضوية
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Text mb={3}>يفقد العضو عضويته في مجلس الحي في إحدى الحالات التالية:</Text>
                  <UnorderedList spacing={2} pr={4} mb={4}>
                    <ListItem>إذا فقد شرطاً من شروط العضوية المنصوص في المادة الرابعة</ListItem>
                    <ListItem>إذا تغيب بدون عذر مقبول عن حضور ثلاث جلسات متتالية أو خمس متقطعة</ListItem>
                    <ListItem>الانسحاب بطلب كتابي</ListItem>
                    <ListItem>الوفاة</ListItem>
                    <ListItem>إذا تأخر عن تسديد الاشتراك لمدة ثلاثة شهور من بداية السنة الميلادية بعد إخطاره بخطاب</ListItem>
                    <ListItem>إذا أقدم العضو على تصرف من شأنه أن يلحق ضرراً مادياً أو أدبياً للمركز أو للجمعية، ولم يلتزم بلوائح ومعايير العمل المؤسسي وحوكمة الجمعية</ListItem>
                    <ListItem>إذا قام العضو باستغلال عضويته لغرض شخصي</ListItem>
                    <ListItem>إذا تعذر عليه القيام بدوره في مجلس الحي لسبب صحي أو أي أسباب أخرى</ListItem>
                  </UnorderedList>
                  
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    إجراءات فقدان العضوية:
                  </Heading>
                  <Text>
                    يرفع مدير الفرع توصياته بفقد العضو عضويته في مجلس الحي وتحديد البديل بخطاب رسمي إلى الإدارة الرئيسية، ومن ثم يتم إصدار القرار من الرئيس التنفيذي.
                  </Text>
                </Box>
              </Box>
            
            {/* Article 6 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة السادسة: مهام مجلس الحي
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <UnorderedList spacing={2} pr={4}>
                    <ListItem>المساهمة في تعزيز اللحمة الوطنية والانتماء للدين والولاء للمليك والوطن</ListItem>
                    <ListItem>المساهمة في تعزيز الأمن الاجتماعي والفكري والوقاية من السلوكيات السلبية</ListItem>
                    <ListItem>تعزيز المسؤولية الاجتماعية لدى أفراد المجتمع وتفعيل العمل التطوعي ونشر ثقافته</ListItem>
                    <ListItem>المساهمة في تفعيل البرامج والأنشطة المقدمة من مركز الحي</ListItem>
                    <ListItem>المساهمة في تعزيز القيم الإيجابية في المجتمع</ListItem>
                    <ListItem>المساهمة في تحسين البيئة ورفع مستوى الخدمات بالحي بالتنسيق مع الجهات المختصة</ListItem>
                    <ListItem>المساهمة في تنمية الموارد المالية للمركز واستثمارها</ListItem>
                    <ListItem>المساهمة في تعزيز العلاقة بين المركز والمجتمع المحلي</ListItem>
                    <ListItem>المساهمة في تعزيز الشراكات المجتمعية مع الجهات الحكومية والأهلية والخيرية</ListItem>
                    <ListItem>المساهمة في إيجاد الحلول للظواهر السلبية في الحي ورفعها للجهات المختصة</ListItem>
                  </UnorderedList>
                </Box>
              </Box>
            
            {/* Article 7 */}
            <Box mb={8}>
              <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                المادة السابعة: مدة عضوية المجلس مركز الحي
              </Heading>
              <Box 
                p={5} 
                bg={cardBg} 
                borderRadius="md" 
                boxShadow="sm"
              >
                <Text>
                  تجدد العضوية سنوياً وبحد أقصى (4) أربع سنوات (بمدة دورة مجلس إدارة الجمعية) وبتحقق شروط اكتسابها وبعد موافقة الجمعية على ذلك.
                </Text>
              </Box>
            </Box>
              
              {/* Article 8 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الثامنة: مهام رئيس مجلس مركز الحي ومؤشرات أدائه
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    المعلومات الوظيفية
                  </Heading>
                  <UnorderedList spacing={2} pr={4} mb={4}>
                    <ListItem><Text as="span" fontWeight="bold">المسمى الوظيفي:</Text> رئيس/ة مجلس الحي</ListItem>
                    <ListItem><Text as="span" fontWeight="bold">المسؤول المباشر:</Text> مدير الفرع</ListItem>
                    <ListItem><Text as="span" fontWeight="bold">نطاق المسؤولية:</Text> (مجلس الحي – مدير/ة المركز)</ListItem>
                  </UnorderedList>
                  
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    مهام رئيس مجلس الحي ومؤشرات الأداء
                  </Heading>
                  
                  <Box overflowX="auto" mb={4}>
                    <Box as="table" width="100%" borderWidth="1px" borderRadius="md">
                      <Box as="thead" bg={tableHeadBg}>
                        <Box as="tr">
                          <Box as="th" p={3} textAlign="right" borderBottomWidth="1px">المهمة</Box>
                          <Box as="th" p={3} textAlign="right" borderBottomWidth="1px">مؤشر الأداء</Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تمثيل المجلس أمام الجهات ذات العلاقة فيما يتعلق بشؤون المقر</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">
                            <UnorderedList spacing={1} pr={2}>
                              <ListItem>وجود عقد (وثيقة) تثبت أحقية المركز</ListItem>
                              <ListItem>الحصول على رخصة المقر</ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تطبيق اشتراطات معايير إيجاد مقر مناسب للمركز</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">
                            <UnorderedList spacing={1} pr={2}>
                              <ListItem>لا تقل نسبة اكتمال تطبيق الاشتراطات عن 80%</ListItem>
                              <ListItem>وجود لوحة للمقر</ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">الدعوة لانعقاد اجتماعات مجلس الحي الدورية</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا تقل عدد الاجتماعات عن 12 اجتماع</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">رئاسة اجتماعات مجلس الحي والإشراف على مهام مجلس الحي</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">
                            <UnorderedList spacing={1} pr={2}>
                              <ListItem>لا تقل عدد محاضر الاجتماعات عن 12 محضر</ListItem>
                              <ListItem>نسبة تنفيذ توصيات المحاضر لا تقل عن 80%</ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تفعيل دور أعضاء المجلس لتحقيق أهداف الجمعية</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا يقل عدد اللجان عن 3 لجان</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تحديد اللجان الجديدة التي يحتاجها المركز</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا يزيد عدد اللجان الجديدة عن لجنتين وتتوافق مع احتياجات المركز</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">التوقيع على الخطابات الصادرة من المركز</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">عدم وجود تأخيرات أو مشكلات ناتجة عن عملية التوقيع</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تنفيذ قرارات الجمعية ومتابعة إلزام كافة أعضاء المجلس بها</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا يقل معدل تنفيذ قرارات الجمعية عن 100%</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">حل المشكلات وإزالة العقبات التي تواجه إدارة المركز</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا يقل رضا إدارة الفرع عن 80%</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">الإشراف على برامج وأنشطة مركز الحي</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا تقل نسبة الإشراف على برامج أنشطة المركز عن 70%</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تقديم أفكار ومقترحات لمصادر تمويل مبتكرة</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">قيمة التمويل الإضافي لا تقل نسبته عن 40% من دخل المركز</Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">متابعة تجديد عضوية مجلس الحي وزيادة أعداد الجمعية العمومية</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">
                            <UnorderedList spacing={1} pr={2}>
                              <ListItem>نسبة تسديد أعضاء مجلس الحي خلال الربع الأول 100%</ListItem>
                              <ListItem>لا يقل عدد الأعضاء المسددين الجدد عن 40 عضو</ListItem>
                              <ListItem>نسبة عدد الأعضاء المسددين لا تقل عن 90%</ListItem>
                              <ListItem>لا يقل استقطاب عدد الأعضاء الداعمين عن اثنين</ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">تعريف الجهات الداعمة المحتملة بدور المركز</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">
                            <UnorderedList spacing={1} pr={2}>
                              <ListItem>لا يقل عدد الشراكات الفاعلة عن 6 شراكات خلال العام</ListItem>
                              <ListItem>مشاركة المركز في الفعاليات الجماهيرية الخارجية لا يقل عن 4 فعاليات</ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                        <Box as="tr" _hover={{ bg: tableHoverBg }}>
                          <Box as="td" p={3} borderBottomWidth="1px">أي مهام أخرى ضمن أنشطته وصلاحياته</Box>
                          <Box as="td" p={3} borderBottomWidth="1px">لا تقل نسبة تأدية المهام المسندة عن 80%</Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              {/* Article 9 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة التاسعة: التزامات أعضاء مجلس الحي
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <Text mb={3}>يلتزم عضو مجلس الحي بالالتزامات المترتبة على عضويته ومنها ما يأتي:</Text>
                  <UnorderedList spacing={2} pr={4}>
                    <ListItem>الالتزام بسداد رسوم العضوية خلال الربع الأول من العام حتى لا تسقط عضويته من مجلس الحي</ListItem>
                    <ListItem>الالتزام بلوائح وأنظمة الجمعية والمهام المطلوبة من مجلس الحي لخدمة سكان الحي</ListItem>
                    <ListItem>الالتزام بحضور اجتماعات مجلس الحي بشكل دائم ومنتظم والمشاركة في مناقشاتها والتصويت على القرارات، ولا يجوز له التفويض في ذلك</ListItem>
                    <ListItem>الالتزام بإيجاد مقر للمركز واستقطاب داعمين لتوفير المقر</ListItem>
                    <ListItem>الالتزام برئاسة وعضوية اللجان التي يكلفه بها المجلس</ListItem>
                    <ListItem>الالتزام بتمثيل المركز أمام الجهات ذات العلاقة بعد تكليف رئيس مجلس الحي له</ListItem>
                    <ListItem>الالتزام بخدمة المركز وإفادته بخبراته ومعارفه واقتراح المواضيع وتقديم المبادرات التي من شأنها النهوض بالمركز</ListItem>
                    <ListItem>الالتزام باحترام أسرار العمل وخصوصيته بما لا يضر بمصلحة الجمعية وعدم استخدام المعلومات في الإساءة للآخرين أو لمصالح خاصة</ListItem>
                    <ListItem>الالتزام بعدم المطالبة بأية رواتب أو مكافآت أو امتيازات مالية لقاء عضويته بالمجلس</ListItem>
                    <ListItem>الالتزام بأخلاقيات العمل والقيم الاجتماعية والمهنية</ListItem>
                    <ListItem>الالتزام بعدم الدخول في أعمال تجارية أو مالية مع المركز وعلى عضو مجلس الحي أن يبلغ المجلس بما له من مصلحة شخصية في هذه الأعمال ويثبت هذا التبليغ في محضر الاجتماع ولا يجوز له الاشتراك في التصويت على القرار الذي يصدر بهذا الشأن</ListItem>
                    <ListItem>الالتزام بعدم التقدم لعضوية مجلس الحي لأكثر من دورتين على التوالي</ListItem>
                  </UnorderedList>
                </Box>
              </Box>
              
              {/* Article 10 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة العاشرة: صلاحيات رئيس مجلس مركز الحي
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  mb={6}
                  boxShadow="sm"
                >
                  <UnorderedList spacing={2} pr={4} mb={4}>
                    <ListItem>الاطلاع على خطط مركز الحي وأنشطته وفعالياته وإبداء الرأي حيالها بشكل مكتوب يرفع لمدير فرع الجمعية من خلال مدير مركز الحي</ListItem>
                    <ListItem>الاطلاع وتحليل الخدمات والأنشطة التي يقدمها المركز بشكل مكتوب يرفع لمدير فرع الجمعية من خلال مدير مركز الحي</ListItem>
                    <ListItem>مناقشة الخطة التشغيلية مع مدير المركز قبل الرفع بها إلى مدير فرع الجمعية لاعتمادها</ListItem>
                    <ListItem>المشاركة في تنفيذ الخطة التشغيلية لمركز الحي، والرفع بالتقارير لمدير فرع الجمعية من خلال مدير مركز الحي</ListItem>
                    <ListItem>رفع القرارات والمقترحات التي تصدر عن المجلس إلى مدير فرع الجمعية من خلال مدير مركز الحي</ListItem>
                    <ListItem>يحق للمجلس إسناد لأحد أعضائه بإدارة المركز تطوعاً ولمدة محدودة لحين تحديد مدير للمركز</ListItem>
                    <ListItem>يحق لرئيس مجلس مركز الحي دعوة الأعضاء لأي اجتماع طارئ، ولا ينعقد المجلس لمركز الحي إلا بحضور أكثر من نصف الأعضاء ورئيس المجلس أو من ينوب عنه ولا يجوز تفويض عضو عن عضو آخر</ListItem>
                  </UnorderedList>
                  
                  <Heading as="h4" size="sm" mb={3} color={headingColor}>
                    صدور القرارات:
                  </Heading>
                  <Text>
                    تصدر القرارات بأغلبية أصوات الحاضرين، وفي حالة التساوي يكون صوت الرئيس مرجحاً.
                  </Text>
                </Box>
              </Box>
              
              {/* Article 11 */}
              <Box mb={8}>
                <Heading as="h3" size="md" mb={3} color={subheadingColor}>
                  المادة الحادية عشر: مميزات عضوية المجلس
                </Heading>
                <Box 
                  p={5} 
                  bg={cardBg} 
                  borderRadius="md" 
                  boxShadow="sm"
                >
                  <UnorderedList spacing={2} pr={4}>
                    <ListItem>حضور اللقاءات الدورية التي تنظمها الجمعية لجميع الأعضاء</ListItem>
                    <ListItem>الحصول على خصم خاص في حال رغب باستئجار أحد مرافق المركز</ListItem>
                    <ListItem>الاستفادة من تخفيضات رسوم الاشتراك التي تتيحها المراكز على فعالياتها وخدماتها</ListItem>
                    <ListItem>الإعفاء عن دفع رسوم اشتراك أحد أنشطة البرامج المقدمة في المركز (يختار نشاط واحد في كل عام)</ListItem>
                    <ListItem>الحصول على النشرات الدورية للجمعية</ListItem>
                    <ListItem>حضور الحفل السنوي للجمعية</ListItem>
                    <ListItem>احتساب ساعات في التطوع الاحترافي، وتسجيلها لعضو المجلس في منصة التطوع</ListItem>
                  </UnorderedList>
                </Box>
              </Box>
            </Box>
            ) : null}
          </SlideFade>
        </Skeleton>
      </VStack>
    </Container>
  );
};

export default NeighborhoodCouncilRegulations;
